import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { controlToastState } from "risa-oasis-ui_v2";
import { fetchDrugConfigfromFirebase } from "../../../../../redux/slice/drugConfigSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { MedicationListItem } from "../data-model/medicationListModel";
import { rowSignature } from "../utils/medicationListHelpers";
import { saveMedicationList } from "../utils/medicationListService";

interface UseMedicationListResult {
  loading: boolean;
  saving: boolean;
  organization: string;
  setOrganization: (org: string) => void;
  medications: MedicationListItem[];
  lastSync: string;
  dirtyCount: number;
  isDirty: (name: string) => boolean;
  updateRow: (name: string, patch: Partial<MedicationListItem>) => void;
  appendMedication: (item: MedicationListItem) => Promise<void>;
  hasNameInList: (candidate: string) => boolean;
  saveChanges: () => Promise<void>;
  refresh: () => void;
  resetEdits: () => void;
}

export const useMedicationList = (
  initialOrg: string,
): UseMedicationListResult => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading } = useSelector((state: RootState) => state.drugConfig);

  const [organization, setOrganization] = useState<string>(initialOrg);
  const [editedMedications, setEditedMedications] = useState<
    MedicationListItem[]
  >([]);
  const [originalSignatures, setOriginalSignatures] = useState<
    Record<string, string>
  >({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchDrugConfigfromFirebase(organization));
  }, [dispatch, organization]);

  // When fresh data arrives, snapshot it as both edited state and the baseline signatures.
  useEffect(() => {
    const fromStore = (data?.medications || []) as MedicationListItem[];
    setEditedMedications(fromStore.map((m) => ({ ...m })));
    const sigs: Record<string, string> = {};
    fromStore.forEach((m) => {
      sigs[m.name] = rowSignature(m);
    });
    setOriginalSignatures(sigs);
  }, [data]);

  const updateRow = useCallback(
    (name: string, patch: Partial<MedicationListItem>) => {
      setEditedMedications((prev) =>
        prev.map((m) => (m.name === name ? { ...m, ...patch } : m)),
      );
    },
    [],
  );

  const isDirty = useCallback(
    (name: string) => {
      const current = editedMedications.find((m) => m.name === name);
      if (!current) return false;
      const baseline = originalSignatures[name];
      // New rows (no baseline) are considered already-saved unless explicitly dirty;
      // appendMedication persists immediately, so this branch returns false then.
      if (baseline === undefined) return false;
      return rowSignature(current) !== baseline;
    },
    [editedMedications, originalSignatures],
  );

  const dirtyCount = useMemo(
    () => editedMedications.filter((m) => isDirty(m.name)).length,
    [editedMedications, isDirty],
  );

  const hasNameInList = useCallback(
    (candidate: string) => {
      const lower = candidate.trim().toLowerCase();
      return editedMedications.some((m) => m.name === lower);
    },
    [editedMedications],
  );

  const saveChanges = useCallback(async () => {
    if (dirtyCount === 0 || saving) return;
    setSaving(true);
    try {
      const stamp = moment().toDate().toISOString();
      const merged = editedMedications.map((m) =>
        isDirty(m.name) ? { ...m, last_updated: stamp } : m,
      );
      await saveMedicationList(organization, merged);
      controlToastState("medication-list-updated-successfully");
      dispatch(fetchDrugConfigfromFirebase(organization));
    } catch (e) {
      controlToastState("medication-list-update-failure");
    } finally {
      setSaving(false);
    }
  }, [dirtyCount, saving, editedMedications, isDirty, organization, dispatch]);

  const appendMedication = useCallback(
    async (item: MedicationListItem) => {
      const next = [...editedMedications, item].sort((a, b) =>
        (a.display_name || "").localeCompare(b.display_name || ""),
      );
      setSaving(true);
      try {
        await saveMedicationList(organization, next);
        controlToastState("drug-name-added-successfully");
        dispatch(fetchDrugConfigfromFirebase(organization));
      } catch (e) {
        controlToastState("drug-name-add-failure");
      } finally {
        setSaving(false);
      }
    },
    [editedMedications, organization, dispatch],
  );

  const refresh = useCallback(() => {
    dispatch(fetchDrugConfigfromFirebase(organization));
  }, [dispatch, organization]);

  const resetEdits = useCallback(() => {
    const fromStore = (data?.medications || []) as MedicationListItem[];
    setEditedMedications(fromStore.map((m) => ({ ...m })));
  }, [data]);

  return {
    loading,
    saving,
    organization,
    setOrganization,
    medications: editedMedications,
    lastSync: data?.last_sync || "",
    dirtyCount,
    isDirty,
    updateRow,
    appendMedication,
    hasNameInList,
    saveChanges,
    refresh,
    resetEdits,
  };
};
